// The files a sent prompt carried (Poulpe-429), under his words: a chip each
// with its name and size. Retained image references display across reloads.
import { useEffect, useState, type SVGProps } from 'react'
import { useStore } from '../store/context'
import type { SentFile } from '../api/types'

/** A size as a reader says it: `812 B`, `99 kB`, `1.2 MB`. */
export function bytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1000) return `${Math.round(bytes)} B`
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} kB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

export function FileIcon({ size = 14, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" {...rest}>
      <path d="M4.25 2.25h4.5l3 3v8.5h-7.5z M8.75 2.25v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  )
}

/** Nothing while the prompt carried none. */
export function SentFiles({ files }: { files: readonly SentFile[] | undefined }) {
  if (!files || files.length === 0) return null
  return (
    <ul className="code-files" title="Files are kept with this session, including after it ends." aria-label={files.length === 1 ? 'File attached' : `${files.length} files attached`}>
      {files.map((file, index) => (
        <li key={`${index}:${file.name}`} className="code-file">
          {file.id && file.mime_type.startsWith('image/') ? <SentImage file={file} /> : <FileIcon />}
          <span className="code-attachment-name" title={file.name}>
            {file.name}
          </span>
          <span className="code-attachment-size">{bytesText(file.bytes)}</span>
        </li>
      ))}
    </ul>
  )
}

/** Authenticated bytes, fetched again after reload/resume. Never a token in an image URL. */
function SentImage({ file }: { file: SentFile }) {
  const store = useStore()
  const [url, setUrl] = useState<string | null>(null)
  const [missing, setMissing] = useState(false)
  useEffect(() => {
    let current = true
    let made: string | null = null
    setUrl(null)
    setMissing(false)
    void store.promptFile(file.id!).then((blob) => {
      if (!current) return
      made = URL.createObjectURL(blob)
      setUrl(made)
    }).catch(() => { if (current) setMissing(true) })
    return () => { current = false; if (made) URL.revokeObjectURL(made) }
  }, [store, file.id])
  return url ? <img className="code-sent-image" src={url} alt={file.name} loading="lazy" />
    : missing ? <span className="muted">Image unavailable</span> : <FileIcon />
}
